/**
 * Lightbox pro náhledy galerie. Renderuje se přes portál do <body>, aby ho
 * nedusily overflow/transform rodiče. Otevírá se z Product.tsx (klik na hlavní
 * náhled), index galerie je řízený zvenku, ať po zavření zůstane vybraný stejný
 * obrázek. Zavírá se křížkem, klikem na pozadí i klávesou Escape; mezi obrázky
 * přepínají šipky po stranách a klávesy ArrowLeft/ArrowRight (s přetečením dokola).
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { galleryImageUrl } from '../lib/api';

type LightboxImage = { id: number };

type LightboxProps = {
  images: LightboxImage[];
  index: number;
  slug: string;
  title: string;
  onIndexChange: (index: number) => void;
  onClose: () => void;
};

export default function Lightbox({
  images,
  index,
  slug,
  title,
  onIndexChange,
  onClose,
}: LightboxProps) {
  const reduce = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const hasMany = images.length > 1;

  // Bezpečný index (kdyby přišel mimo rozsah).
  const safeIndex = ((index % images.length) + images.length) % images.length;
  const current = images[safeIndex];

  const goPrev = () => onIndexChange((safeIndex - 1 + images.length) % images.length);
  const goNext = () => onIndexChange((safeIndex + 1) % images.length);

  // Zamknout scroll na pozadí a zneaktivnit zbytek aplikace po dobu otevření.
  // Portál žije v <body> mimo #root, takže inert na #root uvězní fokus i čtečku
  // uvnitř dialogu (fokus nemůže utéct do stránky pod ním). V úklidu vše vrátíme.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const appRoot = document.getElementById('root');
    const scrollbar = window.innerWidth - html.clientWidth;
    const prevOverflow = html.style.overflow;
    const prevBodyPadding = body.style.paddingRight;
    html.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`; // ať se stránka neposune
    appRoot?.setAttribute('inert', '');
    appRoot?.setAttribute('aria-hidden', 'true');
    return () => {
      html.style.overflow = prevOverflow;
      body.style.paddingRight = prevBodyPadding;
      appRoot?.removeAttribute('inert');
      appRoot?.removeAttribute('aria-hidden');
    };
  }, []);

  // Po otevření dej focus na křížek (ať funguje Escape i klávesnice hned).
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Klávesy: Escape zavírá, šipky přepínají, Tab je uvězněný uvnitř dialogu.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (hasMany && e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (hasMany && e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Tab') {
        // Jednoduchá past: drž focus mezi ovládacími tlačítky dialogu.
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        const activeEl = document.activeElement;
        if (e.shiftKey && activeEl === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMany, safeIndex, images.length]);

  const chipClass =
    'grid h-11 w-11 place-items-center rounded-full bg-chalk/10 text-chalk transition hover:bg-chalk/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chalk/60';

  // Prázdná galerie by neměla nastat (Product.tsx otevírá lightbox jen s obrázky),
  // ale ať je typově i běhově bezpečný.
  if (!current) return null;

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Náhledy: ${title}`}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-board-deep/95 p-4 backdrop-blur-sm"
    >
      {/* Počítadlo vlevo nahoře */}
      {hasMany && (
        <span
          aria-live="polite"
          className="absolute left-4 top-4 text-sm font-semibold text-chalk/80"
        >
          {safeIndex + 1} / {images.length}
        </span>
      )}

      {/* Křížek vpravo nahoře */}
      <button
        ref={closeRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Zavřít náhled"
        className={`absolute right-4 top-4 ${chipClass}`}
      >
        <X className="h-5 w-5" strokeWidth={2} />
      </button>

      {/* Šipky po stranách (jen když je víc obrázků) */}
      {hasMany && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Předchozí náhled"
            className={`absolute left-4 top-1/2 -translate-y-1/2 ${chipClass}`}
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Další náhled"
            className={`absolute right-4 top-1/2 -translate-y-1/2 ${chipClass}`}
          >
            <ChevronRight className="h-5 w-5" strokeWidth={2} />
          </button>
        </>
      )}

      {/* Obrázek: klik na něj dialog nezavírá */}
      <AnimatePresence mode="wait">
        <motion.img
          key={current.id}
          src={galleryImageUrl(slug, current.id)}
          alt={`Náhled ${safeIndex + 1}: ${title}`}
          onClick={(e) => e.stopPropagation()}
          initial={reduce ? false : { opacity: 0, scale: 0.98 }}
          animate={reduce ? undefined : { opacity: 1, scale: 1 }}
          exit={reduce ? undefined : { opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="max-h-[88svh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
        />
      </AnimatePresence>
    </div>,
    document.body,
  );
}
